/**
 * Common translations - English
 * Includes: shared action words, layout, login, tagsView, system title
 */
export default {
  common: {
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    default: 'Default',
    setAsDefault: 'Set as default',
    search: 'Search',
    searchPlaceholder: 'Enter keyword to search',
    select: 'Select',
    available: 'Available',
    reset: 'Reset',
    clear: 'Clear',
    export: 'Export',
    import: 'Import',
    print: 'Print',
    refresh: 'Refresh',
    operation: 'Action',
    status: 'Status',
    remark: 'Remark',
    success: 'Success',
    failed: 'Failed',
    loading: 'Loading...',
    noData: 'No Data',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    enable: 'Enable',
    disable: 'Disable',
    submit: 'Submit',
    back: 'Back',
    close: 'Close',
    view: 'View',
    detail: 'Detail',
    viewAll: 'View All',
    tip: 'Tip',
    more: 'More',
    batchDelete: 'Batch Delete',
    createTime: 'Created',
    updateTime: 'Updated',
    startDate: 'Start Date',
    endDate: 'End Date',
    name: 'Name',
    code: 'Code',
    type: 'Type',
    description: 'Description',
    sort: 'Sort',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    unlimited: 'Unlimited',
    area: 'Area',
    floor: 'Floor',
    selectPlaceholder: 'Please select',
    inputPlaceholder: 'Please enter',
    autoGenerate: 'Auto-generated',
    required: 'Required',
    deleteConfirm: 'Are you sure to delete? This cannot be undone.',
    deleteTitle: 'Delete Confirmation',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    refreshSuccess: 'Refreshed successfully',
    refreshFailed: 'Refresh failed',
    saveSuccess: 'Saved successfully',
    saveFailed: 'Save failed',
    submitSuccess: 'Submitted successfully',
    submitFailed: 'Submit failed',
    operationSuccess: 'Operation successful',
    operationFailed: 'Operation failed',
    noPermission: 'No Permission',
    total: '{total} items',
    selected: '{count} selected',
    normal: 'Normal',
    enabled: 'Enabled',
    disabled: 'Disabled',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    draft: 'Draft',
    active: 'Active',
    terminated: 'Terminated',
    expired: 'Expired',
    unknown: 'Unknown',
    year: 'Year',
    month: 'Month',
    day: 'Day',
    yuan: 'CNY',
    squareMeter: '㎡',
    resetPassword: 'Reset Password',
    resetPasswordConfirm: 'Are you sure to reset this user\'s password?',
    resetPasswordSuccess: 'Password reset successfully',
    // Dynamic form helpers
    inputPrefix: 'Enter ',
    selectPrefix: 'Select ',
    cannotBeEmpty: ' is required',
    remarkPlaceholder: 'Enter remark',
    warning: 'Warning',
    addSuccess: 'Added successfully',
    createSuccess: 'Created successfully',
    editSuccess: 'Edited successfully',
    updateSuccess: 'Updated successfully',
    featureDeveloping: 'Feature under development',
    // Pagination and fullscreen
    seq: 'No.',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    prev: 'Previous',
    next: 'Next',
    downloadSuccess: 'Download successful',
    downloadFailed: 'Download failed',
    comingSoon: 'Coming Soon',
    selectDate: 'Select date',
    hideSearch: 'Hide Search',
    showSearch: 'Show Search',
    creator: 'Creator',
    lastUpdate: 'Last Update',
  },

  theme: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },

  layout: {
    home: 'Home',
    logout: 'Logout',
    profile: 'Profile',
    settings: 'Settings',
    logoutConfirm: 'Are you sure you want to log out?',
    tip: 'Tip',
    menuLoading: 'Loading menu...',
    brandName: 'TwinStar Intelligence',
    collapseMenu: 'Click to collapse menu',
    expandMenu: 'Click to expand menu',
    menuSearch: {
      shortcut: 'Ctrl+K',
      placeholder: 'Search menus...',
      noResult: 'No matching menus found',
      navigate: 'Navigate',
      confirm: 'Confirm',
      close: 'Close',
    },
  },

  login: {
    title: 'Account Login',
    username: 'Username',
    password: 'Password',
    login: 'Login',
    remember: 'Remember password',
    forgotPassword: 'Forgot password?',
    welcomeTitle: 'Welcome back',
    welcome: 'Sign in to continue your work on TwinStar',
    helpPrefix: 'Having account issues? ',
    helpAction: 'Contact system administrator',
    chipCompute: 'Compute Usage',
    chipTasks: 'Inference Tasks',
    brandSubtitle: 'AI-Driven Retail Intelligence Platform',
    usernamePlaceholder: 'Enter username',
    passwordPlaceholder: 'Enter password',
    selectOrg: 'Select organization',
    captchaPlaceholder: 'Enter captcha',
    captcha: 'Captcha',
    clickToGet: 'Click to get',
    loggingIn: 'Logging in...',
    featureSecurity: 'Secure & Reliable',
    featureEfficiency: 'Efficient Operations',
    featureInsight: 'Data Insights',
    copyright: '© 2026 Alayadata · TwinStar Intelligence',
    usernameRequired: 'Please enter username',
    usernameLength: 'Username should be 3-50 characters',
    usernamePattern: 'Username can only contain letters, digits and underscore',
    passwordRequired: 'Please enter password',
    passwordLength: 'Password should be 6-50 characters',
    passwordPattern: 'Password contains invalid characters',
    orgRequired: 'Please select an organization',
    captchaRequired: 'Please enter captcha',
    captchaLength: 'Captcha should be 4 characters',
    loginSuccess: 'Login successful',
    loginFailed: 'Login failed',
    loginError: 'Login error, please try again later',
  },

  tagsView: {
    refresh: 'Refresh',
    close: 'Close',
    closeOther: 'Close Others',
    closeLeft: 'Close Left',
    closeRight: 'Close Right',
    closeAll: 'Close All',
  },

  profile: {
    title: 'Profile',
    basicInfo: 'Basic Info',
    changePassword: 'Change Password',
    username: 'Username',
    realname: 'Name',
    email: 'Email',
    phone: 'Phone',
    gender: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderUnknown: 'Unknown',
    dept: 'Department',
    org: 'Organization',
    role: 'Role',
    avatar: 'Avatar',
    changeAvatar: 'Change Avatar',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    currentPasswordRequired: 'Please enter current password',
    newPasswordRequired: 'Please enter new password',
    confirmPasswordRequired: 'Please confirm new password',
    passwordMismatch: 'Passwords do not match',
    passwordChanged: 'Password changed, please login again',
    profileUpdated: 'Profile updated',
    avatarUpdated: 'Avatar updated',
    avatarUploadFailed: 'Avatar upload failed',
    passwordStrength: 'Password Strength',
    strengthWeak: 'Very Weak',
    strengthLow: 'Weak',
    strengthMedium: 'Medium',
    strengthStrong: 'Strong',
    passwordRule: '8-32 chars, must include letters and digits',
    adminCannotChangePassword: 'Admin password cannot be changed here, please contact super admin',
  },

  system: {
    title: 'TwinStar Intelligence Platform 1.0 · Alayadata',
  },
}
